import { GOOGLE_AUTOMATION_STAGE_STATUSES } from './googleAutomationStageStatuses'
import * as googleUrls from './googleUrls'
import { GoogleSettingKey } from '../../recommendations/recommendationSettingsKeys'
import { StageType, GoogleRecommendationKey } from '../../utils/enums'
import { StageDef } from '../automationMachineInterfaces'
import SectionNotFoundException from '../exception/sectionNotFoundException'
import { evaluateXpath } from '../interruptibleXpath'
import {
  AutomationErrors,
  defaultCantUndoFn,
  getCurrentValueFnFromKey,
  getErrorIsDisabledAndReasonFn,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'
import { getElement } from '../utils'

const getCurrentValueFn = (key: GoogleSettingKey) =>
  getCurrentValueFnFromKey(key, (value) => value)

const getIsFulfilledFn = (key: GoogleSettingKey) =>
  getIsFulfilledFnFromKey(key, (value) => value === 'Off')

const getCantUndoFn = () => defaultCantUndoFn

function getIsDisabledFn(settingKey: GoogleSettingKey) {
  return (
    automationErrors: Partial<Record<GoogleSettingKey, AutomationErrors>>,
  ) => getErrorIsDisabledAndReasonFn(settingKey)(automationErrors).isDisabled
}

function getIsDisabledReasonFn(settingKey: GoogleSettingKey) {
  return (
    automationErrors: Partial<Record<GoogleSettingKey, AutomationErrors>>,
  ) =>
    getErrorIsDisabledAndReasonFn(settingKey)(automationErrors).isDisabledReason
}

function settingIsUnavailable(): boolean {
  const settingNotAvailableEl = evaluateXpath<HTMLDivElement>(
    document,
    '//div[text()="The setting you are looking for is not available for your account."]',
  )[0]
  return settingNotAvailableEl !== undefined
}

async function getCheckboxEl(): Promise<HTMLDivElement> {
  const checkboxEl = await getElement<HTMLDivElement>(
    document,
    '//div[@role="checkbox"][@aria-label="Based upon my activity, Google may show my profile name, profile photo, and activity in shared endorsements in ads."]',
  )
  return checkboxEl
}

async function getCheckboxValue(): Promise<'Off' | 'On'> {
  const checkboxEl = await getCheckboxEl()
  let value: 'Off' | 'On' = 'Off'
  if (checkboxEl.ariaChecked === 'true') {
    value = 'On'
  }
  return value
}

const gatherStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: 'GATHER_GOOGLE_SHARED_ENDORSEMENTS_SHARED_ENDORSEMENTS',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.sharedEndorsementsUrl.navigateForUser(googleAccountIndex)

      if (settingIsUnavailable()) {
        await userContext.updateStoredAutomationErrors(
          {
            [GoogleSettingKey.SHARED_ENDORSEMENTS]: {
              gatherError: {
                message:
                  "Shared endorsements are not available for the user's account",
                name: SectionNotFoundException.exceptionName,
              },
            },
          },
          GoogleRecommendationKey.GOOGLE_SHARED_ENDORSEMENTS,
        )
        await userContext.updateStoredInitialValues(
          {},
          GoogleRecommendationKey.GOOGLE_SHARED_ENDORSEMENTS,
        )
        return
      }
      const value = await getCheckboxValue()
      await userContext.updateStoredInitialValues(
        {
          [GoogleSettingKey.SHARED_ENDORSEMENTS]: value,
        },
        GoogleRecommendationKey.GOOGLE_SHARED_ENDORSEMENTS,
      )
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: 'UPDATE_GOOGLE_SHARED_ENDORSEMENTS_SHARED_ENDORSEMENTS',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.sharedEndorsementsUrl.navigateForUser(googleAccountIndex)

      if (
        !userContext.settingsSelected?.[GoogleSettingKey.SHARED_ENDORSEMENTS]
      ) {
        return
      }
      if (settingIsUnavailable()) {
        return
      }
      const currentValue = await getCheckboxValue()
      const targetValue = 'Off'
      if (currentValue !== targetValue && !userContext.isDry) {
        const checkboxEl = await getCheckboxEl()
        checkboxEl.click()
        await getElement(document, '//div[text()="Updated"]')
      }
    },
  },
]

const undoStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: 'UNDO_GOOGLE_SHARED_ENDORSEMENTS_SHARED_ENDORSEMENTS',
    status: GOOGLE_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    action: async (_machineContext, userContext) => {
      const { googleAccountIndex } = userContext
      googleUrls.sharedEndorsementsUrl.navigateForUser(googleAccountIndex)

      if (
        !userContext.settingsSelected?.[GoogleSettingKey.SHARED_ENDORSEMENTS]
      ) {
        return
      }
      if (settingIsUnavailable()) {
        return
      }
      const currentValue = await getCheckboxValue()
      const initialValue =
        userContext.initialValues?.[GoogleSettingKey.SHARED_ENDORSEMENTS]
      if (currentValue !== initialValue && !userContext.isDry) {
        const checkboxEl = await getCheckboxEl()
        checkboxEl.click()
        await getElement(document, '//div[text()="Updated"]')
      }
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
